// src/App.js
import React from "react";
import { BrowserRouter, Route, Navigate } from "react-router-dom";
import { AnimatedRoutes, PageWrapper } from "./components/AnimatedRoutes";

import DashboardLayout from "./pages/Dashboard";
import VendorDashboard from "./pages/VendorDashboard";
import Alerts from "./pages/Alerts";
import Vendors from "./pages/Vendors";
import PredictDisruption from "./pages/PredictDisruption";
import ForecastTabs from "./components/ForecastTabs";
import ChatPanel from "./components/chatbot";
import ListingsPage from "./pages/Listings";
import ErrorBoundary from "./components/ErrorBoundary";

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import LandingPage from "./pages/LandingPage";
import AdminLayout from "./pages/admin/AdminLayout";

// Onboarding pages
import OnboardingLayout from "./pages/onboarding/OnboardingLayout";

// Billing/Subscription pages
import PricingPage from "./pages/billing/PricingPage";
import SuccessPage from "./pages/billing/SuccessPage";
import CancelPage from "./pages/billing/CancelPage";
import BillingSettings from "./pages/billing/BillingSettings";
import TeamSettings from "./pages/settings/TeamSettings";
import SecuritySettings from "./pages/settings/SecuritySettings";
import SubscriptionGate from "./components/SubscriptionGate";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ThemeProvider } from "./context/ThemeContext";

import "@fullcalendar/common/main.css";
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";

import { isAuthenticated } from "./utils/auth";
import { FEATURES } from "./config";

// Auth guard (login only — used by /onboarding, /pricing)
const ProtectedRoute = ({ children }) => {
  const isLoggedIn = isAuthenticated();
  return isLoggedIn ? children : <Navigate to="/login" replace />;
};

// Full app guard — enforces auth + superadmin redirect + onboarding completion
const AppRoute = ({ children }) => {
  const isLoggedIn = isAuthenticated();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem("agentBlayna_user") || "{}");
  if (user.role === "superadmin") return <Navigate to="/admin" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
};

const SuperadminRoute = ({ children }) => {
  const isLoggedIn = isAuthenticated();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  const user = JSON.parse(localStorage.getItem("agentBlayna_user") || "{}");
  if (user.role !== "superadmin") return <Navigate to="/app" replace />;
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>

          <AnimatedRoutes>
            {/* Root is Landing (public) */}
            <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />

            {/* Login */}
            <Route path="/login" element={<PageWrapper><LoginPage /></PageWrapper>} />
            <Route path="/register" element={<PageWrapper><RegisterPage /></PageWrapper>} />
            {/* Open signup: same page as /register, without an invite code */}
            <Route path="/signup" element={<PageWrapper><RegisterPage /></PageWrapper>} />

            {/* Onboarding (protected, for new users) */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <PageWrapper><OnboardingLayout /></PageWrapper>
                </ProtectedRoute>
              }
            />

            {/* Billing/Subscription pages (only when ENABLE_BILLING=true) */}
            {FEATURES.ENABLE_BILLING && (
              <>
                <Route
                  path="/pricing"
                  element={
                    <ProtectedRoute>
                      <PageWrapper><PricingPage /></PageWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route path="/billing/success" element={<SuccessPage />} />
                <Route path="/billing/cancel" element={<CancelPage />} />
                <Route path="/subscription/success" element={<SuccessPage />} />
                <Route path="/subscription/cancel" element={<CancelPage />} />
              </>
            )}

            {/* Full app (protected + optionally subscription gated) */}
            <Route
              path="/app"
              element={
                <AppRoute>
                  <PageWrapper>
                    {FEATURES.ENABLE_BILLING ? (
                      <SubscriptionProvider>
                        <SubscriptionGate>
                          <DashboardLayout />
                        </SubscriptionGate>
                      </SubscriptionProvider>
                    ) : (
                      <DashboardLayout />
                    )}
                  </PageWrapper>
                </AppRoute>
              }
            >
              {/* Default page after login */}
              <Route index element={<VendorDashboard />} />
              <Route path="dashboard" element={<VendorDashboard />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="vendors" element={<Vendors />} />

              {/* Client Pipeline */}
              <Route path="predict-disruption" element={<PredictDisruption />} />

              {/* Commissions & Revenue */}
              <Route path="commission-forecast" element={<ForecastTabs />} />

              {/* Optional legacy paths */}
              <Route path="predict" element={<PredictDisruption />} />
              <Route path="forecast" element={<ForecastTabs />} />

              <Route path="chat" element={<ChatPanel />} />
              <Route path="listings" element={<ListingsPage />} />

              {/* Account security — intentionally outside the billing flag:
                  changing your password must not depend on billing being on */}
              <Route path="security" element={<SecuritySettings />} />

              {/* Settings (only when billing enabled) */}
              {FEATURES.ENABLE_BILLING && (
                <>
                  <Route path="billing" element={<BillingSettings />} />
                  <Route path="team" element={<TeamSettings />} />
                </>
              )}

              {/* Any unknown path inside /app redirects to VendorDashboard */}
              <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
            </Route>

            {/* Superadmin Panel */}
            <Route path="/admin/*" element={
              <SuperadminRoute><AdminLayout /></SuperadminRoute>
            } />

            {/* Any unknown URL goes home (landing) */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </AnimatedRoutes>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
